import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { customToast } from '../utils/customToast';
import { profileService } from '../services/profileService';
import { X, User, Lock, Eye, EyeOff, Camera } from 'lucide-react';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  viewOnly?: boolean;
}

interface ProfileFormData {
  full_name: string;
  email: string;
  phone: string;
  address: string;
  username: string;
  shop_name: string;
}

interface PasswordFormData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({
  isOpen,
  onClose,
  viewOnly = false
}) => {
  const { theme } = useTheme();
  const { user, refreshUser } = useAuth();
  const { refreshSettings } = useSettings();
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const isDemoUser = user?.username === 'abu.pos';

  // Profile form state
  const [profileData, setProfileData] = useState<ProfileFormData>({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    username: '',
    shop_name: ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [profileErrors, setProfileErrors] = useState<Partial<ProfileFormData>>({});
  const [passwordErrors, setPasswordErrors] = useState<Partial<PasswordFormData>>({});

  // Profile picture state
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [currentProfilePicture, setCurrentProfilePicture] = useState<string | null>(null);

  useEffect(() => {
    if (user && isOpen) {
      console.log('ProfileModal: Loading user data into form:', user);
      setProfileData({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        username: user.username || '',
        shop_name: user.shop?.shop_name || ''
      });

      // Set current profile picture if exists
      if (user.profile_picture_url) {
        setCurrentProfilePicture(user.profile_picture_url);
      } else if (user.profile_picture) {
        // Fallback to manual URL construction using production API
        const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
        const storageBase = apiBase.replace('/api', '/storage');
        setCurrentProfilePicture(`${storageBase}/${user.profile_picture}`);
      }
    }
  }, [user, isOpen]);

  const validateProfile = (): boolean => {
    const newErrors: Partial<ProfileFormData> = {};

    if (!profileData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!profileData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setProfileErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = (): boolean => {
    const newErrors: Partial<PasswordFormData> = {};

    if (!passwordData.current_password) {
      newErrors.current_password = 'Current password is required';
    }

    if (!passwordData.new_password) {
      newErrors.new_password = 'New password is required';
    } else if (passwordData.new_password.length < 8) {
      newErrors.new_password = 'Password must be at least 8 characters long';
    }

    if (!passwordData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your new password';
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));

    if (profileErrors[name as keyof ProfileFormData]) {
      setProfileErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));

    if (passwordErrors[name as keyof PasswordFormData]) {
      setPasswordErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleProfilePictureSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        customToast.error('Please select a valid image file (JPG, PNG, GIF, WebP)');
        return;
      }

      // Validate file size (2MB max)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        customToast.error('File size must be less than 2MB');
        return;
      }

      setProfilePicture(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfilePicture = () => {
    setProfilePicture(null);
    setProfilePicturePreview(null);
    // Reset file input
    const fileInput = document.getElementById('profile-picture-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateProfile()) {
      customToast.error('Please fix the validation errors');
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if shop name was changed
      const shopNameChanged = user?.shop?.shop_name !== profileData.shop_name;

      console.log('Submitting profile update with data:', profileData);

      const response = await profileService.updateProfile(profileData, profilePicture || undefined);

      console.log('Profile updated, response:', response);

      // Update user data in context and localStorage immediately with response data
      if (response.data) {
        localStorage.setItem('user_data', JSON.stringify(response.data));
      }

      // Trigger updates in parallel for faster execution
      const updates = [refreshUser()];
      if (shopNameChanged) {
        console.log('Shop name changed, refreshing settings...');
        updates.push(refreshSettings());
      }

      await Promise.all(updates);

      console.log('All updates complete!');
      customToast.success('Profile updated successfully!');
      onClose();
    } catch (error) {
      console.error('Failed to update profile:', error);
      customToast.error(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePassword()) {
      customToast.error('Please fix the validation errors');
      return;
    }

    setIsSubmitting(true);

    try {
      await profileService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        new_password_confirmation: passwordData.confirm_password
      });
      customToast.success('Password changed successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      onClose();
    } catch (error) {
      console.error('Failed to change password:', error);
      customToast.error(error instanceof Error ? error.message : 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 150);
  };

  if (!isOpen && !isClosing) return null;

  return createPortal(
    <div
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] overflow-y-auto ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'
        }`}
      onClick={handleOverlayClick}
    >
      <div className={`w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-lg shadow-2xl my-auto flex flex-col ${isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
        } ${theme === 'dark' ? 'bg-gray-800 border border-gray-600' : 'bg-white'
        }`}>
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 ${theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
          }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'
                }`}>
                <User className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-slate-900'
                  }`}>
                  Profile Settings
                </h2>
                <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  Manage your account information
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${theme === 'dark'
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className={`flex border-b ${theme === 'dark' ? 'border-slate-700' : 'border-slate-200'
          }`}>
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 py-4 text-sm font-medium transition-all duration-200 relative justify-center ${activeTab === 'profile'
              ? theme === 'dark'
                ? 'text-blue-400 bg-slate-800/50'
                : 'text-blue-600 bg-blue-50/50'
              : theme === 'dark'
                ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <User className={`w-4 h-4 ${activeTab === 'profile' ? 'scale-110' : ''} transition-transform`} />
              <span>Profile Information</span>
            </div>
            {activeTab === 'profile' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
            )}
          </button>

          {!viewOnly && (
            <button
              onClick={() => setActiveTab('password')}
              disabled={user?.username === 'abu.pos'}
              className={`flex-1 py-4 text-sm font-medium transition-all duration-200 relative justify-center ${activeTab === 'password'
                ? theme === 'dark'
                  ? 'text-blue-400 bg-slate-800/50'
                  : 'text-blue-600 bg-blue-50/50'
                : theme === 'dark'
                  ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                } ${user?.username === 'abu.pos' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Lock className={`w-4 h-4 ${activeTab === 'password' ? 'scale-110' : ''} transition-transform`} />
                <span>Change Password</span>
              </div>
              {activeTab === 'password' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
              )}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 140px)' }}>
          <div className="p-2.5 sm:p-3 md:p-4">
            {activeTab === 'profile' ? (
              <form id="profile-form" onSubmit={handleProfileSubmit}>
                {isDemoUser && (
                  <div className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg border ${theme === 'dark'
                    ? 'bg-amber-900/20 border-amber-700/50'
                    : 'bg-amber-50 border-amber-200'
                    }`}>
                    <div className="flex items-start sm:items-center space-x-3">
                      <Lock className={`w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0 ${theme === 'dark' ? 'text-amber-500' : 'text-amber-600'}`} />
                      <div>
                        <h4 className={`text-sm font-semibold ${theme === 'dark' ? 'text-amber-200' : 'text-amber-800'
                          }`}>
                          Demo Account Restricted
                        </h4>
                        <p className={`text-xs sm:text-sm mt-0.5 ${theme === 'dark' ? 'text-amber-300/80' : 'text-amber-700/80'
                          }`}>
                          You cannot update profile information in this demo account.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <fieldset disabled={viewOnly || isDemoUser} className="space-y-4 sm:space-y-6 border-0 min-w-0 p-0 m-0">
                  {/* Profile Picture Section */}
                  <div className="flex flex-col items-center space-y-3 sm:space-y-4 pb-4 sm:pb-6 border-b border-slate-200 dark:border-slate-600">
                    <div className="relative">
                      <div className={`h-20 w-20 sm:h-24 sm:w-24 rounded-full flex items-center justify-center overflow-hidden ${theme === 'dark'
                        ? 'bg-gradient-to-br from-blue-600 to-blue-700'
                        : 'bg-gradient-to-br from-blue-600 to-blue-700'
                        }`}>
                        {profilePicturePreview || currentProfilePicture ? (
                          <img
                            src={profilePicturePreview || currentProfilePicture || ''}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-xl sm:text-2xl font-medium text-white">
                            {user?.full_name?.charAt(0).toUpperCase() || 'A'}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => document.getElementById('profile-picture-upload')?.click()}
                        className={`absolute -bottom-1 -right-1 h-7 w-7 sm:h-8 sm:w-8 rounded-full flex items-center justify-center transition-colors touch-manipulation ${theme === 'dark'
                          ? 'bg-slate-700 hover:bg-slate-600 border-2 border-slate-800'
                          : 'bg-white hover:bg-slate-50 border-2 border-white shadow-lg'
                          }`}
                      >
                        <Camera className={`h-3 w-3 sm:h-4 sm:w-4 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                          }`} />
                      </button>
                    </div>

                    <div className="text-center">
                      <input
                        type="file"
                        id="profile-picture-upload"
                        accept="image/*"
                        onChange={handleProfilePictureSelect}
                        className="hidden"
                      />
                      <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                        }`}>
                        Profile Picture
                      </p>
                      <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-500'
                        }`}>
                        Click the camera icon to upload a new picture
                      </p>
                      {(profilePicturePreview || profilePicture) && (
                        <button
                          type="button"
                          onClick={removeProfilePicture}
                          className={`mt-2 text-xs px-3 py-1.5 rounded-lg transition-colors touch-manipulation ${theme === 'dark'
                            ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20'
                            : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                            }`}
                        >
                          Remove Picture
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="pt-2">
                    <h3 className={`text-base font-medium flex items-center space-x-2 mb-4 ${theme === 'dark' ? 'text-white' : 'text-slate-900'
                      }`}>
                      <User className="w-5 h-5" />
                      <span>Personal Details</span>
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                          }`}>
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="full_name"
                          value={profileData.full_name}
                          onChange={handleProfileChange}
                          disabled={viewOnly}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg border transition-colors touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed ${profileErrors.full_name
                            ? 'border-red-500 focus:border-red-500'
                            : theme === 'dark'
                              ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500'
                              : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500 focus:border-blue-500'
                            } focus:ring-0/20 focus:outline-none`}
                          placeholder="Enter your full name"
                        />
                        {profileErrors.full_name && (
                          <p className="mt-1 text-sm text-red-500">{profileErrors.full_name}</p>
                        )}
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                          }`}>
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={profileData.email}
                          onChange={handleProfileChange}
                          disabled={viewOnly}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg border transition-colors touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed ${profileErrors.email
                            ? 'border-red-500 focus:border-red-500'
                            : theme === 'dark'
                              ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500'
                              : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500 focus:border-blue-500'
                            } focus:ring-0/20 focus:outline-none`}
                          placeholder="Enter your email address"
                        />
                        {profileErrors.email && (
                          <p className="mt-1 text-sm text-red-500">{profileErrors.email}</p>
                        )}
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                          }`}>
                          Username
                        </label>
                        <input
                          type="text"
                          name="username"
                          value={profileData.username}
                          onChange={handleProfileChange}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg border transition-colors touch-manipulation ${theme === 'dark'
                            ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500'
                            : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500 focus:border-blue-500'
                            } focus:ring-0/20 focus:outline-none`}
                          placeholder="Enter your username"
                        />
                      </div>

                      {user?.role === 'shop_owner' && (
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                            }`}>
                            Shop Name
                          </label>
                          <input
                            type="text"
                            name="shop_name"
                            value={profileData.shop_name}
                            onChange={handleProfileChange}
                            className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg border transition-colors touch-manipulation ${theme === 'dark'
                              ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500'
                              : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500 focus:border-blue-500'
                              } focus:ring-0/20 focus:outline-none`}
                            placeholder="Enter your shop name"
                          />
                        </div>
                      )}

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                          }`}>
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={profileData.phone}
                          onChange={handleProfileChange}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg border transition-colors touch-manipulation ${theme === 'dark'
                            ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500'
                            : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500 focus:border-blue-500'
                            } focus:ring-0/20 focus:outline-none`}
                          placeholder="Enter your phone number"
                        />
                      </div>

                      <div className="lg:col-span-2">
                        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                          }`}>
                          Address
                        </label>
                        <textarea
                          name="address"
                          value={profileData.address}
                          onChange={handleProfileChange}
                          rows={3}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-lg border transition-colors resize-none touch-manipulation ${theme === 'dark'
                            ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500'
                            : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500 focus:border-blue-500'
                            } focus:ring-0/20 focus:outline-none`}
                          placeholder="Enter your address"
                        />
                      </div>
                    </div>
                  </div>
                </fieldset>
              </form>
            ) : user?.username === 'abu.pos' ? (
              <div className={`p-4 sm:p-6 rounded-lg border-2 ${theme === 'dark'
                ? 'bg-purple-900/20 border-purple-700/50'
                : 'bg-purple-50 border-purple-200'
                }`}>
                <div className="text-center space-y-3">
                  <div className={`text-lg font-semibold ${theme === 'dark' ? 'text-purple-300' : 'text-purple-700'
                    }`}>
                    Demo Account
                  </div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-purple-200' : 'text-purple-600'
                    }`}>
                    Password changes are disabled for demo accounts. This helps keep the demo environment stable for all users.
                  </p>
                </div>
              </div>
            ) : (
              <form id="password-form" onSubmit={handlePasswordSubmit} className="space-y-4 sm:space-y-6">
                <h3 className={`text-base font-medium flex items-center space-x-2 ${theme === 'dark' ? 'text-white' : 'text-slate-900'
                  }`}>
                  <Lock className="w-5 h-5" />
                  <span>Password Security</span>
                </h3>
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                      Current Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        name="current_password"
                        value={passwordData.current_password}
                        onChange={handlePasswordChange}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 pr-12 text-sm rounded-lg border transition-colors touch-manipulation ${passwordErrors.current_password
                          ? 'border-red-500 focus:border-red-500'
                          : theme === 'dark'
                            ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500'
                            : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500 focus:border-blue-500'
                          } focus:ring-0/20 focus:outline-none`}
                        placeholder="Enter your current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 touch-manipulation ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {passwordErrors.current_password && (
                      <p className="mt-1 text-sm text-red-500">{passwordErrors.current_password}</p>
                    )}
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                      New Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="new_password"
                        value={passwordData.new_password}
                        onChange={handlePasswordChange}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 pr-12 text-sm rounded-lg border transition-colors touch-manipulation ${passwordErrors.new_password
                          ? 'border-red-500 focus:border-red-500'
                          : theme === 'dark'
                            ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500'
                            : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500 focus:border-blue-500'
                          } focus:ring-0/20 focus:outline-none`}
                        placeholder="Enter your new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 touch-manipulation ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {passwordErrors.new_password && (
                      <p className="mt-1 text-sm text-red-500">{passwordErrors.new_password}</p>
                    )}
                    <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                      }`}>
                      Password must be at least 8 characters long
                    </p>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                      }`}>
                      Confirm New Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirm_password"
                        value={passwordData.confirm_password}
                        onChange={handlePasswordChange}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 pr-12 text-sm rounded-lg border transition-colors touch-manipulation ${passwordErrors.confirm_password
                          ? 'border-red-500 focus:border-red-500'
                          : theme === 'dark'
                            ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500'
                            : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500 focus:border-blue-500'
                          } focus:ring-0/20 focus:outline-none`}
                        placeholder="Confirm your new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 touch-manipulation ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {passwordErrors.confirm_password && (
                      <p className="mt-1 text-sm text-red-500">{passwordErrors.confirm_password}</p>
                    )}
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className={`flex-shrink-0 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 p-2.5 sm:p-3 md:p-4 border-t ${theme === 'dark'
          ? 'border-slate-700 bg-slate-900/50'
          : 'border-slate-200 bg-slate-50'
          }`}>
          <button
            type="button"
            onClick={handleClose}
            className={`w-full sm:w-auto px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 text-sm rounded-lg font-medium transition-colors touch-manipulation ${theme === 'dark'
              ? 'text-slate-300 hover:text-white hover:bg-slate-700'
              : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
              }`}
          >
            Close
          </button>
          {!viewOnly && !isDemoUser && (
            <button
              type="submit"
              form={activeTab === 'profile' ? 'profile-form' : 'password-form'}
              disabled={isSubmitting}
              className={`w-full sm:w-auto px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 text-sm rounded-lg font-medium text-white transition-colors touch-manipulation ${isSubmitting
                ? 'bg-slate-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                }`}
            >
              {isSubmitting
                ? (activeTab === 'profile' ? 'Updating...' : 'Changing...')
                : (activeTab === 'profile' ? 'Update Profile' : 'Change Password')
              }
            </button>
          )}
        </div>
      </div>
    </div >,
    document.body
  );
};
